import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  FormFeedback,
  Modal,
  Radio,
  useToast
} from '@morressier/alembic';
import { faClockFour } from '@fortawesome/pro-light-svg-icons';
import { IPeerReviewListItemWithRescission } from '@publishing-engine/domain/views';
import {
  useDenyReviewDeadlineExtension,
  useExtendDeadline
} from 'src/api/commands';
import { FormProvider, useForm } from 'react-hook-form';
import {
  decideOnExtensionSchema,
  IDeadlineExtensionDecisionFormData
} from '@publishing-engine/domain/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormFieldDate } from '../../FormField';
import { format } from 'date-fns';
import { getDaysDifference } from '@publishing-engine/submission-service/src/utils/date';
import { formatDate, formatDateSafe } from '../../../utils/date';

interface Props {
  isOpen: boolean;
  organizationId: string;
  review?: IPeerReviewListItemWithRescission;
  onClose: () => void;
}

export const GrantReviewExtensionModal: React.FC<Props> = ({
  isOpen,
  review,
  organizationId,
  onClose
}) => {
  const toast = useToast();
  const currentDeadline = new Date(
    review?.workflow?.currentDeadline ?? new Date()
  );
  const isExtensionRequested =
    review?.workflow.deadlineExtensions?.status === 'requested';

  const newDeadline = isExtensionRequested
    ? review?.workflow?.deadlineExtensions?.proposedDate
      ? review.workflow.deadlineExtensions.proposedDate.toString()
      : undefined
    : currentDeadline
    ? formatDateSafe(currentDeadline)
    : undefined;
  const minDeadline =
    new Date(currentDeadline) < new Date()
      ? formatDateSafe(new Date())
      : formatDateSafe(currentDeadline);
  const methods = useForm<IDeadlineExtensionDecisionFormData>({
    defaultValues: {
      deadline: newDeadline,
      action: !isExtensionRequested ? 'grant' : undefined
    },
    resolver: zodResolver(decideOnExtensionSchema)
  });

  const proposedDate = review?.workflow.deadlineExtensions?.proposedDate;

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting }
  } = methods;

  const extendDeadline = useExtendDeadline({
    organizationId,
    submissionId: review?.submission.id!,
    submissionContextId: review?.submissionContextId!,
    reviewRequestId: review?.id!
  });

  const denyDeadlineExtension = useDenyReviewDeadlineExtension({
    organizationId,
    submissionId: review?.submission.id!,
    reviewRequestId: review?.id!,
    submissionContextId: review?.submissionContextId!
  });

  const mainReviewerNameOrEmail = review?.reviewer?.firstName
    ? `${review?.reviewer?.firstName} ${review?.reviewer?.lastName}`
    : review?.reviewer?.email;

  const coReviewerNameOrEmail = review?.coReview?.coReviewer?.firstName
    ? `${review?.coReview?.coReviewer?.firstName} ${review?.coReview?.coReviewer?.lastName}`
    : review?.coReview?.coReviewer?.email;

  const reviewerNameOrEmail = [mainReviewerNameOrEmail, coReviewerNameOrEmail]
    .filter(Boolean)
    .join(' & ');

  const actionError = errors.action;
  const action = watch('action');
  const deadline = watch('deadline');

  const DEFAULT_ERROR_MESSAGE = 'Please refresh and try again.';

  const getErrorDescription = (error: unknown): string => {
    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    if (error && typeof error === 'object') {
      const errorObject = error as { message?: unknown };

      if (
        typeof errorObject.message === 'string' &&
        errorObject.message.trim().length > 0
      ) {
        return errorObject.message;
      }
    }

    return DEFAULT_ERROR_MESSAGE;
  };

  const grantExtension = async (deadlineDays: number) => {
    const response = await extendDeadline.mutateAsync({
      deadlineDays,
      source: isExtensionRequested ? 'request' : 'manual'
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Deadline Extended',
        description: `${reviewerNameOrEmail} must submit their review by ${format(
          new Date(deadline),
          'MMM dd, yyyy'
        )}.`,
        isClosable: true
      });
      return true;
    }

    toast({
      variant: 'error',
      title: 'Unable to process extension request',
      description: getErrorDescription(response.message),
      isClosable: true
    });

    return false;
  };

  const denyExtension = async () => {
    const response = await denyDeadlineExtension.mutateAsync();

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Extension Request Rejected',
        description: `${reviewerNameOrEmail} will be informed of your decision via email.`,
        isClosable: true
      });
      return true;
    }

    toast({
      variant: 'error',
      title: 'Unable to process extension request',
      description: getErrorDescription(response.message),
      isClosable: true
    });

    return false;
  };

  const onSubmit = async (data: IDeadlineExtensionDecisionFormData) => {
    let isSuccess = false;

    if (data.action === 'grant') {
      const daysToAdd = getDaysDifference(
        new Date(currentDeadline!),
        new Date(data.deadline)
      );

      isSuccess = await grantExtension(daysToAdd);
    } else {
      isSuccess = await denyExtension();
    }

    if (isSuccess) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="720px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={6} padding={8}>
            <Flex>
              <FontAwesomeIcon fontSize={32} icon={faClockFour} />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">
                {isExtensionRequested ? 'Extension Request' : 'Extend Deadline'}
              </Text>

              {isExtensionRequested && (
                <>
                  <Text textStyle="textBody">
                    {reviewerNameOrEmail} need
                    {!coReviewerNameOrEmail ? 's' : ''} more time to complete
                    their task.
                  </Text>
                  {proposedDate && (
                    <Text textStyle="headingItem">
                      Proposed Date: {formatDate(new Date(proposedDate))}
                    </Text>
                  )}
                </>
              )}
            </Flex>
            <Flex flexDirection={'column'} gap={4}>
              {isExtensionRequested && (
                <Radio
                  {...register('action')}
                  value="grant"
                  ariaLabel="approve extension"
                  id="grant"
                >
                  <Text textStyle="headingItem">Approve Extension Request</Text>
                  <Text>
                    You will be able to pick a new deadline date for this
                    requester's task before confirming this decision.
                  </Text>
                </Radio>
              )}

              {(action === 'grant' || !isExtensionRequested) && (
                <FormFieldDate
                  config={{
                    name: 'deadline',
                    component: 'Custom:DateInput',
                    caseDataFields: {
                      main: 'deadline'
                    },
                    params: {
                      labels: {
                        main: 'New Deadline Date',
                        subtitle: ''
                      },
                      validations: {
                        kind: 'date',
                        required: true,
                        min: minDeadline
                      }
                    }
                  }}
                />
              )}

              {isExtensionRequested && (
                <Radio
                  {...register('action')}
                  value="deny"
                  ariaLabel="reject extension"
                  id="deny"
                >
                  <Text textStyle="headingItem">Reject Extension Request</Text>
                  <Text>
                    The requester will have to submit their work under the
                    current deadline. They will be informed of your decision via
                    email.
                  </Text>
                </Radio>
              )}

              {actionError && (
                <FormFeedback variant="error">
                  {actionError.message}
                </FormFeedback>
              )}
            </Flex>
            <Flex justifyContent="flex-end" gap={2}>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                Confirm Decision
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
